import React from "react";
import { cn } from "@/lib/utils";
import { Box, FileText, Store, Copy } from "lucide-react";
import {
  buildGoogleSearchUrl,
  buildWalmartSearchUrl,
  buildRakutenSearchUrl,
  buildAmazonSearchUrl,
  buildWalmartProductUrl,
} from "@/lib/analyzerData";
import { trustedMerchantDirectUrl } from "@/components/buy-sources/normalizeSource";
import { visibleBuySourcesFromEntry } from "@/lib/storefrontHunter/autoBuySources";

export const PRODUCT_SOURCE_LINE = "#dfe7f0";

/** Exact Analyzer brand marks — single source of truth. */
export const ProductSourceBrandSvg = {
  walmart: (
    <svg viewBox="0 0 1345 1512" className="h-4 w-4" aria-hidden="true"><g fill="#ffc220"><path d="m949.3 688.9c31.6-6.5 311.2-134.2 336.1-148.6 57-32.8 76.5-105.4 43.6-162.2-32.9-56.8-105.8-76.3-162.7-43.5-25 14.4-275.6 191.9-297 216-24.6 27.5-28.8 66.9-10.7 98.3 18.2 31.4 54.5 47.4 90.7 40z"/><path d="m1285.4 971.7c-25-14.4-304.5-142.1-336.1-148.6-36.2-7.4-72.5 8.6-90.7 40-18.1 31.4-13.9 70.8 10.7 98.3 21.4 24 272.1 201.6 297 215.9 57 32.9 129.8 13.4 162.7-43.4 32.9-56.8 13.3-129.4-43.6-162.2z"/><path d="M672.6 970.2c-36.3 0-68.4 23.3-80.1 58.3-10.1 30.6-39 335.8-39 364.5 0 65.6 53.3 118.8 119.1 118.8 65.8 0 119.1-53.2 119.1-118.8 0-28.7-28.8-334-39-364.5-11.6-35-43.7-58.3-80.1-58.3z"/><path d="m395.9 823.1c-31.6 6.5-311.2 134.2-336.1 148.6-57 32.8-76.4 105.4-43.6 162.2 32.9 56.8 105.8 76.3 162.7 43.4 25-14.3 275.6-191.9 297.1-215.9 24.5-27.6 28.8-66.9 10.6-98.3-18.2-31.4-54.5-47.4-90.7-40z"/><path d="M178.9 334.6C121.9 301.8 49.1 321.3 16.2 378.1-16.7 434.9 2.9 507.5 59.8 540.3c25 14.4 304.5 142.1 336.1 148.6 36.2 7.4 72.5-8.6 90.7-40 18.2-31.4 13.9-70.8-10.6-98.3-21.5-24.1-272.1-191.6-297.1-216z"/><path d="M672.6.2c-65.7 0-119.1 53.1-119.1 118.7 0 28.8 28.9 334 39 364.6 11.7 35 43.8 58.3 80.1 58.3 36.4 0 68.5-23.4 80.1-58.3 10.2-30.6 39-335.8 39-364.6C791.7 53.4 738.4.2 672.6.2z"/></g></svg>
  ),
  google: (
    <svg viewBox="0 0 1556 1587" className="h-4 w-4" aria-hidden="true"><path fill="#4285f4" d="M1555 811c0-65-5-113-16-162H794v294h437c-9 73-57 183-162 257l250 194c150-138 236-342 236-583z"/><path fill="#34a853" d="M794 1587c214 0 394-71 525-193l-250-194c-67 47-157 80-275 80-210 0-388-139-452-330L85 1149c130 259 398 438 709 438z"/><path fill="#fbbc05" d="M342 950c-17-49-26-102-26-157s9-107 25-157L85 437C31 545 0 665 0 793s31 249 85 356z"/><path fill="#eb4335" d="M794 307c149 0 249 64 306 118l224-219C1187 78 1008 0 794 0 483 0 215 178 85 437l256 199c65-191 243-329 453-329z"/></svg>
  ),
  amazon: (
    <svg viewBox="0 0 1801 1529" className="h-4 w-4" aria-hidden="true"><path fill="#111827" d="M866 3c188-12 403 50 434 291 8 90 0 361 10 531 9 56 38 96 75 138 10 14 13 36-2 49l-152 130c-19 16-36 21-61 1-50-41-79-82-113-127-97 95-198 129-339 129-173 0-308-107-308-322 0-168 90-282 218-338 111-49 267-57 388-71 0-59 4-109-30-150-37-46-109-52-161-13-31 23-44 57-51 97-4 18-17 39-37 36l-235-25c-20-4-42-21-36-49C519 93 726 12 866 3zm150 605c-106 3-220 23-270 99-45 69-34 185 27 231 64 48 160 18 206-57 43-70 37-171 37-273z"/><path fill="#ff6200" d="M10 1159c10-5 20 2 29 7 375 216 795 320 1165 206 139-31 266-80 398-134 15-6 36 1 40 17 4 13-6 25-16 32-259 187-564 260-836 237-286-24-562-142-774-331-12-11-21-24-6-34z"/></svg>
  ),
  rakuten: (
    <svg viewBox="0 0 1494 1590" className="h-4 w-4" aria-hidden="true"><g fill="#bf1818"><path d="m213 1590-213-179h1494z"/><path d="M213 1277V1h401c243 0 440 197 440 440 0 148-73 279-185 359l359 477H924L627 881H456v396zm243-638h158c109 0 198-89 198-198s-89-198-198-198H456z"/></g></svg>
  ),
};

/**
 * Exact Analyzer icon order (8):
 * Catalog, Details, Store, Compare, Walmart, Google, Amazon, Rakuten
 *
 * Slot ids are stable for Analyzer actions:
 * - walmart = Lucide Store icon (Analyzer: Walmart PDP)
 * - wm-marketplace = Walmart brand (Analyzer: marketplace search)
 */
export const PRODUCT_SOURCE_ITEMS = [
  { id: "catalog", title: "Catalog", Icon: Box, ariaLabel: "View product sizes" },
  { id: "details", title: "Product details", Icon: FileText, ariaLabel: "View product description" },
  { id: "walmart", title: "Store", Icon: Store, ariaLabel: "Store" },
  { id: "compare", title: "Compare sources", Icon: Copy },
  { id: "wm-marketplace", title: "Walmart", brand: "walmart", ariaLabel: "Walmart" },
  { id: "google", title: "Google", brand: "google" },
  { id: "amazon", title: "Amazon", brand: "amazon" },
  { id: "retailer", title: "Rakuten", brand: "rakuten" },
];

export const PRODUCT_SOURCE_ICON_IDS = PRODUCT_SOURCE_ITEMS.map((i) => i.id);

/** First trusted merchant PDP from a Buy Sources entry — never Google/reference. */
export function firstTrustedMerchantPdpFromBuySources(entry) {
  for (const row of visibleBuySourcesFromEntry(entry || {})) {
    const href = trustedMerchantDirectUrl(row);
    if (href) return href;
  }
  return null;
}

function titleSearchLinks(titleForSearch) {
  return {
    google: buildGoogleSearchUrl(titleForSearch),
    amazon: buildAmazonSearchUrl(titleForSearch),
    rakuten: buildRakutenSearchUrl(titleForSearch),
    walmartSearch: buildWalmartSearchUrl(titleForSearch),
  };
}

/**
 * Analyzer MatchHeader defaults:
 * - Store slot → Walmart PDP
 * - Walmart brand → marketplace title search
 * Catalog / details / compare overridden by MatchHeader popovers.
 */
export function buildTitleSourceLinkActions({
  title,
  walmartItemId,
  walmartUrl,
} = {}) {
  const titleForSearch = String(title || "").trim() || null;
  const wUrl = walmartUrl || buildWalmartProductUrl(walmartItemId);
  const links = titleSearchLinks(titleForSearch);
  return {
    catalog: { enabled: false },
    details: { enabled: false },
    walmart: { enabled: Boolean(wUrl), href: wUrl || undefined },
    compare: { enabled: false },
    "wm-marketplace": {
      enabled: Boolean(links.walmartSearch),
      href: links.walmartSearch || undefined,
    },
    google: { enabled: Boolean(links.google), href: links.google || undefined },
    amazon: { enabled: Boolean(links.amazon), href: links.amazon || undefined },
    retailer: { enabled: Boolean(links.rakuten), href: links.rakuten || undefined },
  };
}

/**
 * Storefront Hunter card actions:
 * - Store slot → trusted merchant PDP only (PR17)
 * - Walmart brand → Walmart PDP builder
 * - Google / Amazon / Rakuten → shared title builders
 * Catalog / details / compare stay visible; enabled only when callers pass actions.
 */
export function buildStorefrontHunterSourceActions({
  title,
  walmartItemId,
  buySourcesEntry = null,
  catalogAction = null,
  detailsAction = null,
  compareAction = null,
} = {}) {
  const titleForSearch = String(title || "").trim() || null;
  const walmartPdp = buildWalmartProductUrl(walmartItemId);
  const trustedStore = firstTrustedMerchantPdpFromBuySources(buySourcesEntry);
  const links = titleSearchLinks(titleForSearch);
  return {
    catalog: catalogAction || { enabled: false },
    details: detailsAction || { enabled: false },
    // Store icon: trusted merchant PDP only — never Google/reference.
    walmart: { enabled: Boolean(trustedStore), href: trustedStore || undefined },
    compare: compareAction || { enabled: false },
    // Walmart brand: real Walmart PDP (not marketplace search on SFH).
    "wm-marketplace": { enabled: Boolean(walmartPdp), href: walmartPdp || undefined },
    google: { enabled: Boolean(links.google), href: links.google || undefined },
    amazon: { enabled: Boolean(links.amazon), href: links.amazon || undefined },
    retailer: { enabled: Boolean(links.rakuten), href: links.rakuten || undefined },
  };
}

/** Shared Analyzer / SFH icon bar — no compact/SFH restyle variants. */
export function ProductSourceIconBar({
  active = null,
  onSelect,
  actions,
  buttonRefs,
  detailsRef,
  catalogRef,
  className,
  "aria-label": ariaLabel = "Product data source",
}) {
  return (
    <nav
      className={cn(
        "flex gap-1 items-center m-0 shrink-0 min-w-0 w-full sm:w-auto overflow-x-auto overscroll-x-contain py-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      aria-label={ariaLabel}
      data-testid="product-source-icon-bar"
      onClick={(e) => e.stopPropagation()}
    >
      {PRODUCT_SOURCE_ITEMS.map((item) => {
        const action = actions?.[item.id] || { enabled: false };
        const isActive = active === item.id;
        const enabled = Boolean(action?.enabled);
        const label = item.ariaLabel || item.title;
        const setRef = (el) => {
          if (buttonRefs) {
            if (el) buttonRefs.current[item.id] = el;
            else delete buttonRefs.current[item.id];
          }
          if (item.id === "details" && detailsRef) detailsRef.current = el;
          if (item.id === "catalog" && catalogRef) catalogRef.current = el;
        };
        const common = cn(
          "w-[29px] h-[29px] rounded-[7px] border grid place-items-center p-0 transition-[transform,border-color,background,box-shadow] duration-150 ease-out shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2864e8]/40 focus-visible:ring-offset-2",
          isActive
            ? "bg-[#2864e8] border-[#2864e8] text-white shadow-[0_4px_9px_#2864e830]"
            : "bg-white text-[#52647a]",
          enabled && !isActive && "hover:border-[#aebed0] hover:-translate-y-px cursor-pointer",
          !enabled && "opacity-45 cursor-not-allowed",
        );
        const style = isActive ? undefined : { borderColor: PRODUCT_SOURCE_LINE };
        const LucideIcon = item.Icon;
        const brand = item.brand ? ProductSourceBrandSvg[item.brand] : null;
        const icon = brand
          ? React.cloneElement(brand, { className: "h-4 w-4" })
          : <LucideIcon className="h-4 w-4" aria-hidden />;
        if (!enabled) {
          return (
            <button
              key={item.id}
              ref={setRef}
              type="button"
              className={common}
              style={style}
              title={item.title}
              aria-label={label}
              aria-pressed={isActive}
              disabled
              data-testid={`product-source-${item.id}`}
            >
              {icon}
            </button>
          );
        }
        if (action.href) {
          return (
            <a
              key={item.id}
              ref={setRef}
              href={action.href}
              target="_blank"
              rel="noopener noreferrer"
              className={common}
              style={style}
              title={item.title}
              aria-label={`${label} (opens in new tab)`}
              aria-pressed={isActive}
              data-testid={`product-source-${item.id}`}
              onClick={() => onSelect?.(item.id)}
            >
              {icon}
            </a>
          );
        }
        return (
          <button
            key={item.id}
            ref={setRef}
            type="button"
            className={common}
            style={style}
            title={item.title}
            aria-label={label}
            aria-pressed={isActive}
            aria-expanded={action.ariaExpanded}
            aria-controls={action.ariaControls}
            data-testid={action.testId || `product-source-${item.id}`}
            onClick={() => {
              onSelect?.(item.id);
              action.onClick?.();
            }}
          >
            {icon}
          </button>
        );
      })}
    </nav>
  );
}

export default ProductSourceIconBar;
